/**
 * Shared Product Map domain vocabulary.
 *
 * The product map gives an org a legible map of its product (areas →
 * capabilities) and links tests (workflows) to capabilities so coverage is
 * showable. Populated primarily by the planning agent, edited by humans.
 *
 * @module product-map
 */

/**
 * Lifecycle of an area, capability or rule. `deprecated` is a soft delete: the row stays
 * addressable and, critically, keeps owning its natural code (see
 * `packages/db/src/repositories/product-map/codes.ts`). Codes are meant to be
 * citable in tickets and specs forever, which is only true if nothing is ever
 * re-issued — so the product vocabulary is "deprecate", not "delete".
 */
export const PRODUCT_MAP_STATUSES = ['in_dev', 'alpha', 'beta', 'ga', 'deprecated'] as const;
export type ProductMapStatus = (typeof PRODUCT_MAP_STATUSES)[number];

/** Lifecycle order is explicit; PostgreSQL enum sort order is not the stage order. */
export const PRODUCT_MAP_STAGE_RANK: Record<ProductMapStatus, number> = {
  deprecated: 0,
  in_dev: 1,
  alpha: 2,
  beta: 3,
  ga: 4,
};

/** Normalize legacy status in proposal acceptance, open-proposal comparison, and history field diffs. */
export function normalizeProductMapRevisionStatus(status: unknown): ProductMapStatus | undefined {
  if (status === 'active') return 'ga';
  return PRODUCT_MAP_STATUSES.find((stage) => stage === status);
}

/** Who last wrote a product-map row: the planning agent, or a human editor. */
export const PRODUCT_MAP_SOURCES = ['agent', 'user'] as const;
export type ProductMapSource = (typeof PRODUCT_MAP_SOURCES)[number];

/**
 * Capability priority, ordered most to least urgent. Deliberately the bare
 * `p0`…`p3` shorthand rather than adjectives: every engineering org already
 * reads a p-level unambiguously, whereas "critical" vs "high" invites debate
 * about which is which. `p2` is the default for a newly created capability.
 */
export const PRODUCT_MAP_IMPORTANCES = ['p0', 'p1', 'p2', 'p3'] as const;
export type ProductMapImportance = (typeof PRODUCT_MAP_IMPORTANCES)[number];

/** Entity kind a `product_map_revisions` row (or a lock) refers to. */
export const PRODUCT_MAP_ENTITY_TYPES = ['area', 'capability', 'rule'] as const;
export type ProductMapEntityType = (typeof PRODUCT_MAP_ENTITY_TYPES)[number];

export const PRODUCT_MAP_CHANGE_KINDS = [
  'created',
  'updated',
  'deprecated',
  'proposal_rejected',
  /** A capability's code changed — it moved area, or its area's code was edited. */
  'recoded',
  'persona_access',
] as const;
export type ProductMapChangeKind = (typeof PRODUCT_MAP_CHANGE_KINDS)[number];

export const PRODUCT_MAP_ACTOR_KINDS = ['user', 'agent'] as const;
export type ProductMapActorKind = (typeof PRODUCT_MAP_ACTOR_KINDS)[number];

/**
 * Per-capability coverage classification (see `packages/db/src/repositories/product-map/coverage.ts`):
 * - `covered`     — at least one active link to an eligible, non-degraded workflow with a recent pass.
 * - `known_issue` — not covered, every linked workflow is red (never passed, failing or
 *   degraded) or blocked on an open issue (its latest status change cites one), and an
 *   open issue is linked to the capability or one of its rules, or cited by a blocked
 *   workflow: the red is explained, not untriaged. Counted on its own: never as linked (mapped) or covered.
 * - `stale`       — has active links, but every linked workflow is degraded/paused/hasn't passed recently.
 * - `uncovered`   — no active links, or every linked workflow is ineligible.
 * - `blocked`     — coverage cannot be built: a person set `blocked_reason` (an environment or
 *   fixture is missing), or no flow is linked and an open issue is linked (derived). Excluded
 *   from the linked/covered denominators regardless of what (if anything) is linked to it.
 */
export const CAPABILITY_COVERAGE_STATUSES = [
  'covered',
  'known_issue',
  'stale',
  'uncovered',
  'blocked',
] as const;
export type CapabilityCoverageStatus = (typeof CAPABILITY_COVERAGE_STATUSES)[number];

/** Forward-compatible structured payload stored in Product Map JSONB columns. */
export type ProductMapJsonObject = Record<string, unknown>;

/**
 * A single testable rule attached to a capability — previously a markdown
 * bullet under a capability's `## Rules` section, now its own record so
 * agents can add/edit one rule at a time with a per-rule lock. `status`
 * mirrors `ProductMapStatus`: `deprecated` is the soft delete.
 */
export interface CapabilityRule {
  id: string;
  capabilityId: string;
  statement: string;
  status: ProductMapStatus;
  source: ProductMapSource;
  createdAt: string;
  updatedAt: string;
}
